import React, { useState, useEffect } from "react";
import {
  Table,
  Space,
  Typography,
  Col,
  Row,
  Modal,
  Dropdown,
  notification,
  Input
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EllipsisOutlined,
  CopyOutlined
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import Card from "antd/es/card/Card";
import { PrimaryButton } from "../../shared/button/Buttons";
import moment from "moment";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import type { MenuProps } from "antd";

const { confirm } = Modal;

const Datasets: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // New state for clone modal
  const [cloneModalVisible, setCloneModalVisible] = useState<boolean>(false);
  const [cloneDataset, setCloneDataset] = useState<FinetuneDataset | null>(null);
  const [cloneName, setCloneName] = useState<string>("");
  const [cloneDescription, setCloneDescription] = useState<string>("");

  useEffect(() => {
    fetchDatasets();
  }, [currentPage, apiKey]);

  const fetchDatasets = () => {
    const offset = (currentPage - 1) * pageSize;
    DatasetService.getDatasets(offset, pageSize, setLoading, apiKey).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch datasets: ${error}`,
        });
      }
    );
  };

  // New function to open clone modal
  const openCloneModal = (dataset: FinetuneDataset) => {
    setCloneDataset(dataset);
    setCloneName(`${dataset.name} copy`);
    // If dataset has a description property, you may prefill it, otherwise default to empty string
    setCloneDescription(dataset.description || "");
    setCloneModalVisible(true);
  };

  const handleClone = () => {
    if (cloneDataset) {
      const payload = {
        name: cloneName,
        description: cloneDescription
      };
      DatasetService.cloneDataset(cloneDataset.id, payload, setLoading, apiKey).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Dataset cloned successfully",
          });
          setCloneModalVisible(false);
          // Refresh the dataset list
          fetchDatasets();
        },
        (error: any) => {
          console.error("Error cloning dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to clone dataset: ${error}`,
          });
        }
      );
    }
  };

  const showDeleteConfirm = (dataset: FinetuneDataset) => {
    confirm({
      title: "Confirm Delete",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>Are you sure you want to delete dataset "{dataset.name}"?</p>
          <p>This action cannot be undone.</p>
        </>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        // Call the delete endpoint
        DatasetService.deleteDataset(dataset.id, setLoading, apiKey).subscribe(
          () => {
            console.log("Dataset deleted successfully");
            // Refresh the dataset list
            fetchDatasets();
          },
          (error: any) => {
            console.error("Error deleting dataset:", error);
          }
        );
      },
      onCancel() {
        console.log("Delete cancelled");
      },
    });
  };

  const columns: ColumnsType<FinetuneDataset> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (date) =>
        date ? moment(date).format("YYYY-MM-DD hh:mm:ss A") : "",
    },
    {
      title: "Source",
      key: "source",
      width: "30%",
      render: (_, record) => {
        if (record.intent_group_name) {
          return record.intent_group_name;
        } else if (record.application_name && record.intent_name) {
          return `${record.application_name} / ${record.intent_name}`;
        } else {
          return "";
        }
      },
    },
    {
      title: "Request Count",
      dataIndex: "request_count",
      key: "request_count",
      width: "15%",
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "clone",
            icon: <CopyOutlined />,
            label: "Clone",
            onClick: (info) => {
              info.domEvent.stopPropagation();
              openCloneModal(record);
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Delete",
            onClick: (info) => {
              info.domEvent.stopPropagation();
              showDeleteConfirm(record);
            },
          },
        ];

        return (
          <div
            style={{ display: "flex", justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight">
              <a onClick={(e) => e.stopPropagation()}>
                <EllipsisOutlined style={{ fontSize: "16px" }} />
              </a>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <Card className="applications-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              Datasets
            </Typography>
          </Col>
          <Col>
            <PrimaryButton
              title="New Dataset"
              className="buttonWrapper"
              icon={<PlusOutlined />}
              onClick={() => navigate("/models/datasets/new")}
            />
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={datasets}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/models/datasets/${record.id}`),
            style: { cursor: "pointer" },
          })}
        />

        {/* Clone Modal */}
        <Modal
          title="Clone Dataset"
          visible={cloneModalVisible}
          confirmLoading={loading}
          cancelButtonProps={{ disabled: loading }}
          onOk={handleClone}
          onCancel={() => setCloneModalVisible(false)}
          okText="Clone"
          cancelText="Cancel">
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 4 }}>Name</label>
            <Input
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Name"
              disabled={loading}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Description</label>
            <Input.TextArea
              value={cloneDescription}
              onChange={(e) => setCloneDescription(e.target.value)}
              placeholder="Description"
              rows={4}
              disabled={loading}
            />
          </div>
        </Modal>
      </Space>
    </Card>
  );
};

export default Datasets;
